import { useContext } from "@/store/context";
import { flow, initial, last, mapKeys } from "lodash";
import type { RouteRecordRaw } from "vue-router";
import type { FeatureKey } from "@/config/features";

declare module "vue-router" {
  interface RouteMeta {
    authRequired?: boolean;
    roles?: Array<string>;
    privilege?: { resource: string; attribute: string };
    /**
     * Gate the route behind a feature flag. Declared on a parent route
     * cascades to all children (the router guard walks `to.matched`).
     * Disabled flag → redirect to home before the component mounts.
     */
    featureFlag?: FeatureKey;
    /**
     * Pagina di sola utilità, senza scopo in produzione per nessuna variante.
     * Il guard la chiude dove VITE_UTILITY_PAGES_ENABLED non è true.
     */
    utilityPage?: boolean;
  }
}

/**
 * For every folder in 'pages' match the file named as the folder eg pages/Wizard/Wizard.vue
 * The rest of the files are used in the page but need not be made available
 *
 * \\1 is equivalent to \1 in regex, matches the capturing group number 1
 */
const modules = import.meta.glob("@/pages/**/*.vue");
// Takes a path and returns a capitalized module name
const getModuleName = flow([(value, key) => key.split(/[/.]+/g), initial, last]);

// Makes a map of all the pages in the application
const pages = mapKeys(modules, getModuleName);

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: { name: "welcome" },
  },
  {
    path: "/home",
    name: "home",
    component: pages.Home,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/login",
    name: "login",
    component: pages.Login,
  },
  {
    path: "/logged-user-profile",
    name: "logged-user-profile",
    component: pages.LoggedUserProfile,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/placeholder",
    name: "placeholder",
    component: pages.Placeholder,
    meta: {
      authRequired: true,
      utilityPage: true,
    },
  },
  {
    path: "/welcome",
    name: "welcome",
    component: pages.Welcome,
  },
  {
    path: "/wrong-role",
    name: "wrong-role",
    component: pages.WrongRole,
    // meta: {
    //   authRequired: true,
    // },
  },
  {
    path: "/dashboard",
    component: pages.Dashboard,
    meta: {
      authRequired: true,
      privilege: {
        resource: "dashboard_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        // name: "prelead",
        name: "dashboard",
        component: () => import("@/pages/Dashboard/DashboardIndex.vue"),
        meta: {
          privilege: {
            resource: "dashboard_page",
            attribute: "view",
          },
        },
      },
      // {
      //   name: "dashboard-job-detail",
      //   path: "job/detail/:id?",
      //   component: () => import("@/pages/Dashboard/Commercial/JobDetail.vue"),
      // },
    ],
  },
  {
    path: "/notifications",
    name: "notifications",
    component: pages.Notifications,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/translations",
    name: "translations",
    component: pages.Translation,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
      // privilege: {
      //   resource: "traslations_page",
      //   attribute: "view",
      // },
    },
  },
  {
    path: "/translationsv2",
    name: "translationsv2",
    component: pages.Translationv2,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
      // privilege: {
      //   resource: "traslations_page",
      //   attribute: "view",
      // },
    },
  },
  {
    path: "/training",
    name: "training",
    component: pages.Training,
    meta: {
      authRequired: true,
      privilege: {
        resource: "training_page",
        attribute: "view",
      },
    },
  },
  {
    path: "/qrcode",
    // name: "qrcode",
    component: pages.Qrcode,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
      privilege: {
        resource: "qr_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "qrcode",
        component: () => import("@/pages/Qrcode/QrcodeIndex.vue"),
        meta: {
          privilege: {
            resource: "qr_page",
            attribute: "view",
          },
        },
      },
      {
        name: "qrcode-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Qrcode/QrcodeDetail.vue"),
      },
    ],
  },
  {
    path: "/testmfe120395___!",
    component: pages.Mfe,
    meta: {
      authRequired: true,
      utilityPage: true,
    },
    children: [
      {
        path: "",
        name: "testmfe120395___!",
        component: () => import("@/pages/Mfe/MfeDetail.vue"),
        props: {
          mfeName: "entity_app",
          componentPath: "./EntityMicrofrontend",
          context: {},
        },
      },
    ],
  },
  {
    path: "/reti-commerciali",
    component: pages.Mfe,
    meta: {
      authRequired: true,
      privilege: {
        resource: "networks_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "reti-commerciali",
        component: () => import("@/pages/Mfe/MfeDetail.vue"),
        props: {
          mfeName: "graph_app",
          componentPath: "./GraphMicrofrontend",
          context: {},
        },
        meta: {
          privilege: {
            resource: "networks_page",
            attribute: "view",
          },
        },
      },
    ],
  },
  {
    path: "/template-list",
    component: pages.Template,
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
      privilege: {
        resource: "templates_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "template-list",
        component: () => import("@/pages/Template/TemplateList.vue"),
        meta: {
          privilege: {
            resource: "templates_page",
            attribute: "view",
          },
        },
      },
      {
        name: "template-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Template/TemplateEdit.vue"),
        meta: {
          privilege: {
            resource: "templates_page",
            attribute: "view",
          },
        },
      },
    ],
  },
  {
    path: "/template-list-v2",
    component: () => import("@/pages/TemplateV2/TemplateV2.vue"),
    meta: {
      authRequired: true,
      roles: ["BackofficeUser"],
      privilege: {
        resource: "templates_page",
        attribute: "view",
      }
    },
    children: [
      {
        path: "",
        name: "template-list-v2",
        component: () => import("@/pages/TemplateV2/TemplateListV2.vue"),
        meta: {
          privilege: {
            resource: "templates_page",
            attribute: "view",
          },
        },
      },
      {
        name: "template-edit-v2",
        path: "edit/:id?",
        component: () => import("@/pages/TemplateV2/TemplateEditV2.vue"),
        meta: {
          privilege: {
            resource: "templates_page",
            attribute: "view",
          },
        },
      },
    ],
  },
  {
    path: "/switchboard",
    name: "switchboard",
    component: () => import("@/pages/Switchboard/Switchboard.vue"),
    meta: {
      authRequired: true,
      roles: ["Teleselling"],
      privilege: {
        resource: "switchboard_page",
        attribute: "view",
      },
    },
  },
  {
    path: "/switchboard/stats",
    name: "switchboard-stats",
    component: () => import("@/pages/Switchboard/SwitchboardStats.vue"),
    meta: {
      authRequired: true,
      roles: ["Teleselling"],
      privilege: {
        resource: "switchboard_page",
        attribute: "view",
      },
    },
  },
  {
    path: "/switchboard/campaigns",
    name: "switchboard-campaigns",
    component: () => import("@/pages/Switchboard/SwitchboardCampaigns.vue"),
    meta: {
      authRequired: true,
      roles: ["Teleselling"],
      variantFeature: "switchboardCampaigns",
      privilege: {
        resource: "switchboard_page",
        attribute: "view",
      },
    },
  },
  {
    path: "/switchboard/campaigns/detail/:id?",
    name: "switchboard-campaign-detail",
    component: () => import("@/pages/Switchboard/SwitchboardCampaignDetail.vue"),
    meta: {
      authRequired: true,
      roles: ["Teleselling"],
      variantFeature: "switchboardCampaigns",
      privilege: {
        resource: "switchboard_page",
        attribute: "view",
      },
    },
  },
  {
    // Pagina ospitata in una window popup dedicata: contiene SOLO la barra SIP
    // e relativo MediaStream WebRTC. Sopravvive a F5 della tab principale, così
    // una chiamata audio in corso non viene interrotta. Aperta tramite
    // useSipPopupManager dalla main app dopo gesture utente (popup blocker).
    path: "/sip-bar",
    name: "sip-bar",
    component: () => import("@/pages/SipBar/SipBar.vue"),
    meta: {
      authRequired: true,
      roles: ["Teleselling"],
    },
  },
  {
    path: "/org-map",
    redirect: { name: "org-map-organizations" },
    meta: {
      authRequired: true,
      privilege: {
        resource: "map_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "parcels",
        name: "org-map-parcels",
        component: () => import("@/pages/Maps/ParcelsMapPage.vue"),
        meta: {
          authRequired: true,
          privilege: {
            resource: "map_page",
            attribute: "view",
          },
          mapMode: "parcels",
        },
      },
      {
        path: "organizations",
        name: "org-map-organizations",
        component: () => import("@/pages/Maps/OrganizationsMapPage.vue"),
        meta: {
          authRequired: true,
          privilege: {
            resource: "map_page",
            attribute: "view",
          },
          mapMode: "organizations",
        },
      },
    ],
  },
  {
    path: "/additional-agreement",
    name: "additional-agreement",
    component: pages.AdditionalAgreement,
    meta: {
      authRequired: true,
    },
  },
  {
    path: "/bonus",
    component: pages.Bonus,
    meta: {
      authRequired: true,
      privilege: {
        resource: "bonus_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "gestione-bonus",
        component: () => import("@/pages/Bonus/BonusIndex.vue"),
      },
      {
        name: "gestione-bonus-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Bonus/BonusEdit.vue"),
      },
    ],
  },
  {
    path: "/lead",
    component: pages.Lead,
    name: "manageAnagraphic-people",
    redirect: { name: "manageAnagraphic-people-privacyOk" }, // it is the default subroute
    meta: {
      authRequired: true,
      privilege: {
        resource: "leads_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "privacy-ok",
        name: "manageAnagraphic-people-privacyOk",
        component: () => import("@/pages/Leads/LeadsIndex.vue"),
      },
      {
        path: "privacy-ko",
        name: "manageAnagraphic-people-privacyKo",
        component: () => import("@/pages/Leads/LeadsIndex.vue"),
      },
      {
        name: "manageAnagraphic-people-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Leads/LeadsDetail.vue"),
      },
    ],
  },
  // NEW ORGANIZATION
  {
    path: "/organization-pillar",
    component: pages.Organization,
    name: "manageAnagraphic-organizations-page",
    redirect: { name: "manageAnagraphic-organizations" }, // it is the default subroute
    meta: {
      authRequired: true,
      privilege: {
        resource: "organizations_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "manageAnagraphic-organizations",
        component: () => import("@/pages/Organizations/OrganizationsIndex.vue"),
      },
      {
        name: "manageAnagraphic-organizations-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Organizations/OrganizationsDetail.vue"),
      },
    ],
  },
  // ------------------------------------------
  // {
  //   path: "/organization-lead",
  //   component: pages.Organization,
  //   meta: {
  //     authRequired: true,
  //     privilege: {
  //       resource: "organizations_page",
  //       attribute: "view",
  //     },
  //   },
  //   children: [
  //     {
  //       path: "",
  //       name: "manageOrg-lead",
  //       component: () => import("@/pages/Leads/LeadsIndex.vue"),
  //     },
  //     {
  //       name: "manageOrg-lead-edit",
  //       path: "edit/:id?",
  //       component: () => import("@/pages/Leads/LeadsDetail.vue"),
  //     },
  //   ],
  // },
  {
    path: "/intervention-type",
    component: pages.InterventionType,
    meta: {
      authRequired: true,
      privilege: {
        resource: "bonus_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "gestione-intervention",
        component: () => import("@/pages/InterventionType/InterventionTypeIndex.vue"),
      },
    ],
  },
  {
    path: "/configuration",
    component: pages.Configuration,
    meta: {
      authRequired: true,
      privilege: {
        resource: "bonus_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "gestione-configurazione",
        component: () => import("@/pages/Configuration/ConfigurationIndex.vue"),
      },
      {
        name: "gestione-configurazione-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Configuration/ConfigurationEdit.vue"),
      },
    ],
  },
  {
    path: "/opportunities",
    name: "manageOpportunity-root",
    component: pages.Opportunities,
    meta: {
      authRequired: true,
      privilege: {
        resource: "opportunities_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "manageOpportunity-default",
        component: () => import("@/pages/Opportunities/OpportunitiesIndex.vue"),
        meta: {
          privilege: {
            resource: "opportunities_page",
            attribute: "view",
          },
        },
      },
      // dynamic routes (managed in useDynamicOpportunityRoutes composable) will be added here
      {
        name: "manageOpportunity-edit",
        path: "edit/:id?",
        component: () => import("@/pages/Opportunities/OpportunitiesDetail.vue"),
      },
      {
        path: "default",
        name: "manageOpportunity-default-path",
        component: () => import("@/pages/Opportunities/OpportunitiesIndex.vue"),
        meta: {
          privilege: {
            resource: "opportunities_page",
            attribute: "view",
          },
        },
      },
      {
        name: "manageOpportunity-config",
        path: "config",
        component: () => import("@/pages/Opportunities/OpportunitiesConfig.vue"),
        meta: {
          authRequired: true,
          privilege: {
            resource: "opportunities_configuration_page",
            attribute: "view",
          },
        },
        children: [
          {
            name: "manageOpportunity-config-scope",
            path: ":scopeId",
            component: () => import("@/pages/Opportunities/OpportunitiesConfigScope.vue"),
          },
        ],
      },
    ],
  },
  {
    path: "/projects",
    component: pages.Projects,
    meta: {
      authRequired: true,
      privilege: {
        resource: "projects_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "projects",
        component: () => import("@/pages/Projects/ProjectsIndex.vue"),
      },
      // {
      //   name: "projects-edit",
      //   path: "edit/:id?",
      //   component: () => import("@/pages/Projects/ProjectsDetail.vue"),
      // },
    ],
  },
  {
    // GAIA-2623 - Reports section: visibility is driven solely by the reports_page privilege
    path: "/reports",
    component: pages.Reports,
    meta: {
      authRequired: true,
      privilege: {
        resource: "reports_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "reports-default",
        component: () => import("@/pages/Reports/ReportsIndex.vue"),
        meta: {
          privilege: {
            resource: "reports_page",
            attribute: "view",
          },
        },
      },
    ],
  },
  {
    path: "/timesheet",
    component: pages.Timesheet,
    meta: {
      authRequired: true,
      privilege: {
        resource: "timesheet_page",
        attribute: "view",
      },
    },
    children: [
      {
        path: "",
        name: "timesheet",
        component: () => import("@/pages/Timesheet/TimesheetIndex.vue"),
        meta: {
          privilege: {
            resource: "timesheet_page",
            attribute: "view",
          },
        }
      },
    ],
  },
  {
    path: "/LandingOtp/:token",
    name: "landing-otp",
    component: pages.LandingOTP,
  },
  {
    path: "/dev",
    name: "dev",
    component: pages.Dev,
    meta: {
      authRequired: true,
      utilityPage: true,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: { name: "home" },
  },
];

export default routes;
